import { useNavigation } from '@react-navigation/native'
import { useTheme } from 'contexts'
import React, { useEffect, useMemo, useState } from 'react'
import { FlatList, RefreshControl, ScrollView } from 'react-native'

import { NotificationRow } from 'pages/Notifications/subcomponents/NotificationRow'
import { RequestRow } from 'pages/Notifications/subcomponents/RequestRow'

import { EmptyState } from 'partials/EmptyState'
import { Page } from 'partials/Page'

import { Box } from 'widgets/Box'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'

import { useCurrentUser } from 'hooks/useCurrentUser'
import { useMembershipRequests } from 'hooks/useMembershipRequests'
import { useNotifications } from 'hooks/useNotifications'
import { useQueryClient } from 'hooks/useQueryClient'

import { externalLinks, links } from 'utils/constants'
import { handleShareAction } from 'utils/sharing'
import commonStyles from 'utils/styles/commonStyles'

import { Notification } from 'types/notification'

type NavigationPageProps = {
  withBack?: boolean
}

export const NotificationsPage = ({ withBack = true }: NavigationPageProps) => {
  const theme = useTheme()
  const queryClient = useQueryClient()
  const navigation = useNavigation<any>()

  const { data: membershipRequests, isLoading: isLoadingRequests } = useMembershipRequests()

  const { data: currentUser, update: updateUser } = useCurrentUser()
  const [notificationsSeenAt, setNotificationsSeenAt] = useState(
    currentUser?.meta?.notificationsLastSeenTimestamp ?? 0,
  )

  const {
    data: notifications,
    isLoading: isNotificationsLoading,
    isFetching: isNotificationsFetching,
    isFetchingNextPage: isNotificationsFetchingNextPage,
    fetchNextPage: fetchNextNotificationsPage,
  } = useNotifications()

  useEffect(() => {
    if (
      notifications &&
      notifications.length > 0 &&
      notifications[0].timestamp > notificationsSeenAt
    ) {
      setNotificationsSeenAt(notifications[0].timestamp)
      updateUser.mutate({
        data: { meta: { notificationsLastSeenTimestamp: notifications[0].timestamp } },
      })
    } else if (notificationsSeenAt !== 0 && notifications.length === 0) {
      fetchNextNotificationsPage()
    }
  }, [fetchNextNotificationsPage, notifications, notificationsSeenAt, updateUser])

  const refreshing = useMemo(
    () => !notifications || (isNotificationsFetching && !isNotificationsFetchingNextPage),
    [isNotificationsFetching, isNotificationsFetchingNextPage, notifications],
  )

  const onRefresh = React.useCallback(() => {
    queryClient.invalidateQueries(['membershipRequests', currentUser?.id])
    queryClient.invalidateQueries(['programEvents'])
    queryClient.invalidateQueries(['userEvents'])
    queryClient.invalidateQueries(['notifications'])
  }, [currentUser?.id, queryClient])

  const hasContent = useMemo(
    () => membershipRequests?.length || notifications?.length,
    [membershipRequests?.length, notifications?.length],
  )

  const navigateToUpspacesPage = () => {
    navigation.navigate(links.TRAINERS_PAGE)
  }

  const navigateToWorkoutsPage = () => {
    navigation.navigate(links.WORKOUTS_PAGE)
  }

  const handleInviteFriend = async () => {
    const url =
      `${externalLinks.UPSPACE_FULL_PATH}/profile/member/${currentUser?.username}`.toLocaleLowerCase()

    const message = `Check out ${currentUser?.firstName} ${currentUser?.lastName}'s Group!`
    handleShareAction(url, message, navigation, links.SHARE_PROFILE_PAGE)
  }

  return (
    <Page
      withNav
      withBack={withBack}
      isLoading={isLoadingRequests || isNotificationsLoading}
      title={!withBack ? 'Notifications' : 'Activity'}
      backgroundColor='white'
      navigation={navigation}
    >
      <Separator />
      {!hasContent ? (
        <EmptyState
          title='No Activity'
          subtitle='All your activity, including pending memberships, will appear here.'
          actionButtons={[
            {
              title: 'Discover Groups',
              type: 'primary',
              onPress: navigateToUpspacesPage,
            },
            {
              title: 'Find Workouts',
              type: 'secondary',
              onPress: navigateToWorkoutsPage,
            },
            {
              title: 'Invite a Friend',
              type: 'secondary',
              onPress: handleInviteFriend,
            },
          ]}
        />
      ) : (
        <ScrollView
          refreshControl={
            <RefreshControl
              refreshing={refreshing}
              onRefresh={onRefresh}
              progressBackgroundColor={theme.colors.v2primary}
              tintColor={theme.colors.v2primary}
            />
          }
        >
          <Box flex={1} margin={{ top: 'md', horizontal: 'md' }}>
            {membershipRequests?.length ? (
              <>
                <Box>
                  <Text bold color='greyDarker'>
                    Pinned
                  </Text>
                  {membershipRequests?.map((item) => (
                    <RequestRow pendingMembership={item} />
                  ))}
                </Box>
                <Box margin={{ vertical: 'md' }}>
                  <Separator />
                </Box>
              </>
            ) : null}

            <Box>
              <Text bold color='greyDarker'>
                All Activity
              </Text>
              <FlatList<Notification>
                data={notifications}
                loading={isNotificationsLoading}
                onEndReached={fetchNextNotificationsPage}
                renderItem={({ item }) => (
                  <NotificationRow key={item.eventId} notification={item} />
                )}
                keyExtractor={(item) => `Notification-${item.eventId}`}
                testID='NotificationsList'
                contentContainerStyle={commonStyles.flexGrow}
                ListFooterComponent={
                  isNotificationsFetchingNextPage ? (
                    <Box padding={{ bottom: 16 }}>
                      <LoadingSpinner />
                    </Box>
                  ) : null
                }
              />
            </Box>
          </Box>
        </ScrollView>
      )}
    </Page>
  )
}
